// Shared sx styles for Churn & Recovery, following design.md.
export const ACCENT = "#A72C68";
export const ACCENT_DARK = "#7B1C4A";
export const ACCENT_LIGHT = "#FAEFF5";
export const PINK_BORDER = "#F0CDDB";
export const ACCENT_PILL = "#FCE8EC";

export const churnCard = {
  background: "#fff",
  borderRadius: "14px",
  border: "1px solid #F0F1F4",
  boxShadow: "0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)",
  overflow: "hidden",
  mb: "12px",
};

export const pillSelectSx = {
  background: "#fff",
  height: "44px",
  borderRadius: "10px",
  minWidth: 170,
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
    borderWidth: "1.5px",
  },
};

export const dateInputSx = {
  background: "#fff",
  "& .MuiOutlinedInput-root": {
    height: "44px",
    borderRadius: "10px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: ACCENT },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
  },
};

export const outlinedActionBtn = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  background: "#fff",
  border: `1px solid ${color}`,
  color,
  borderRadius: "10px",
  height: "36px",
  px: "12px",
  fontSize: "13px",
  fontFamily: "helvetica-lt-std-bold",
  cursor: "pointer",
  textTransform: "none",
  transition: "all .2s ease",
  "&:hover": {
    background: color,
    color: "#fff",
    transform: "translateY(-1px)",
    boxShadow: `0 4px 10px ${color}33`,
  },
});

// --- CRM customer-list card parity (mirrors CRMPage's crmBanner /
// sectionLabel / statBox / amountBox / buttonRow / aiInsightsBtn) so the
// churn cards read as the same product family. ---

export const cardBanner = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px",
  background: ACCENT_LIGHT,
  px: { xs: "14px", sm: "20px" },
  py: "7px",
};

export const bannerMutedLabel = {
  fontSize: "13px",
  color: "#6B7280",
  fontFamily: "helvetica-lt-std-roman",
};

export const bannerStrongValue = {
  fontSize: "14px",
  color: "#1a1a2e",
  fontFamily: "helvetica-lt-std-bold",
};

export const sectionLabel = {
  fontSize: "11px",
  letterSpacing: "1.2px",
  color: "#6B7280",
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "uppercase",
  mb: "8px",
};

export const statBox = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  background: ACCENT_LIGHT,
  borderRadius: "10px",
  px: "14px",
  py: "10px",
};

export const tintBox = {
  background: ACCENT_LIGHT,
  borderRadius: "12px",
  p: "10px 14px",
};

export const countPill = {
  background: ACCENT_PILL,
  color: ACCENT,
  borderRadius: "20px",
  px: "8px",
  py: "1px",
};

export const cardFooter = {
  background: "#F7F8FA",
  borderTop: "1px solid #F0F1F4",
  px: { xs: "12px", sm: "20px" },
  py: "9px",
};

export const buttonRow = {
  // Mobile: uniform 2-column grid so the actions line up neatly.
  // Tablet/desktop: natural flex-wrap row.
  display: { xs: "grid", sm: "flex" },
  gridTemplateColumns: { xs: "repeat(2, 1fr)" },
  flexWrap: { sm: "wrap" },
  gap: "8px",
  alignItems: "center",
};

// Filled gradient button with a repeating shimmer sweep — the one action on
// the card that should draw the eye (AI Insights), same as the CRM list.
export const aiInsightsBtn = {
  position: "relative",
  overflow: "hidden",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  gap: "8px",
  width: { xs: "100%", sm: "auto" },
  minWidth: { xs: 0, sm: "max-content" },
  background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 45%, #D946EF 100%)`,
  color: "#fff",
  border: "1px solid transparent",
  borderRadius: "10px",
  px: { xs: "10px", sm: "14px" },
  py: 0,
  height: "36px",
  fontSize: { xs: "12px", sm: "13px" },
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "none",
  cursor: "pointer",
  transition: "all 0.18s ease",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  "& .MuiSvgIcon-root": { color: "#fff" },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-80%",
    width: "50%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
    transform: "skewX(-20deg)",
    animation: "aiShimmer 2.6s ease-in-out infinite",
  },
  "@keyframes aiShimmer": {
    "0%": { left: "-80%" },
    "55%": { left: "130%" },
    "100%": { left: "130%" },
  },
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${ACCENT}66`,
  },
  "&:active": { transform: "translateY(0)" },
};

export const statusPill = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  background: "#fff",
  border: `1px solid ${color}`,
  color,
  borderRadius: "20px",
  px: "12px",
  py: "3px",
  fontSize: "12px",
  fontFamily: "helvetica-lt-std-bold",
  whiteSpace: "nowrap",
});

// Churn card variant of statusPill: on extra-small phones the label may be
// long ("3D Lifestyle Etobicoke, ON ( $69 plan new) ...", "Laser Hair Removals
// way overdue · 881d past"), so let the text wrap inside the pill instead of
// spilling out of the banner. Tablet/desktop keep the single-line pill.
export const churnStatusPill = (color) => ({
  ...statusPill(color),
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  whiteSpace: { xs: "normal", sm: "nowrap" },
  overflowWrap: { xs: "anywhere", sm: "normal" },
  lineHeight: 1.3,
  "& .MuiSvgIcon-root": { flexShrink: 0 },
});

export const sectionTitle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "1.15rem",
  fontWeight: 700,
  color: "#111827",
  fontFamily: "helvetica-lt-std-bold",
  "&::before": {
    content: '""',
    width: "4px",
    height: "20px",
    borderRadius: "2px",
    background: ACCENT,
    display: "inline-block",
  },
};

export const drawerPaper = {
  width: { xs: "100%", sm: 480, md: 540 },
  maxWidth: "100vw",
  background: "#fff",
  boxShadow: "-12px 0 40px rgba(0,0,0,0.10)",
  display: "flex",
  flexDirection: "column",
};

export const drawerHeader = {
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  px: 2.5,
  py: 2,
  borderBottom: `1px solid ${PINK_BORDER}`,
  flexShrink: 0,
};

export const drawerIconChip = {
  width: 42,
  height: 42,
  borderRadius: "10px",
  background: ACCENT_LIGHT,
  border: `1px solid ${PINK_BORDER}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const drawerFooter = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 1.25,
  px: 2.5,
  py: 2,
  borderTop: `1px solid ${PINK_BORDER}`,
  flexShrink: 0,
};

export const greenGradientBtn = {
  background: "linear-gradient(135deg, #27AE60 0%, #1E8449 100%)",
  color: "#fff",
  height: 42,
  borderRadius: "10px",
  px: 3,
  fontSize: "14px",
  textTransform: "none",
  "&:hover": { background: "linear-gradient(135deg, #219653 0%, #1E8449 100%)" },
  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
};

export const cancelBtn = {
  background: "#EB5757",
  color: "#fff",
  border: "1px solid #EB5757",
  height: 42,
  borderRadius: "10px",
  px: 3,
  fontSize: "14px",
  textTransform: "none",
  "&:hover": { background: "#fff", color: "#EB5757", border: "1px solid #EB5757" },
};

// Wraps a component that renders its own trigger button (e.g. SendSmsModal):
// the real button is stretched invisibly over the styled label below.
export const modalBtnWrap = (color) => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minWidth: "max-content",
  height: "36px",
  background: "#fff",
  color,
  border: `1px solid ${color}`,
  borderRadius: "10px",
  px: "12px",
  fontSize: "13px",
  fontFamily: "helvetica-lt-std-bold",
  textTransform: "none",
  cursor: "pointer",
  transition: "all 0.18s ease",
  lineHeight: 1.2,
  overflow: "hidden",
  whiteSpace: "nowrap",
  "&:hover": {
    background: color,
    borderColor: color,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 10px ${color}33`,
    "& .mbw-label": { color: "#fff" },
    "& .mbw-label .MuiSvgIcon-root": { color: "#fff" },
  },
  "& .mbw-label": {
    pointerEvents: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    position: "relative",
    zIndex: 2,
    color,
  },
  "& .mbw-label .MuiSvgIcon-root": { color },
  "& button, & .MuiButton-root, & a.MuiButtonBase-root": {
    position: "absolute !important",
    inset: "0 !important",
    width: "100% !important",
    height: "100% !important",
    opacity: "0 !important",
    background: "transparent !important",
    backgroundImage: "none !important",
    boxShadow: "none !important",
    border: "none !important",
    padding: "0 !important",
    margin: "0 !important",
    minWidth: "0 !important",
    minHeight: "0 !important",
    cursor: "pointer",
    zIndex: 1,
    "&:hover": { background: "transparent !important" },
  },
});
